import React from 'react';
import FormInput from '../FormInput';
import { useNavigate } from 'react-router-dom';
import { FeatureState, RoutePaths } from '../../../Constants/Common';
import Selectable from '../Selectable';
import './PolicyHolderInfo.scss';
import { useAppSelector } from 'state/hooks';
import { useTranslation } from 'react-i18next';
import BankDetailsInfo from 'components/payment/BankDetailsInfo';

const PolicyHolderInfo = (): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const features = useAppSelector((s) => s.site.features);
    const showBankDetails = useAppSelector((s) => s.site.showBankDetails);
    const {
        title,
        firstName,
        middleName,
        lastName,
        email,
        addressLine1,
        addressLine2,
        townOrCity,
        region,
        country,
        postcode,
        accountHolderName,
        accountNumber,
        sortCode,
        businessPartnerId
    } = useAppSelector((s) => ({
        title: s.customer.title,
        firstName: s.customer.firstName,
        middleName: s.customer.middleName,
        lastName: s.customer.lastName,
        email: s.customer.email,
        addressLine1: s.customer.address.addressLine1,
        addressLine2: s.customer.address.addressLine2,
        townOrCity: s.customer.address.townOrCity,
        region: s.customer.address.region,
        country: s.customer.address.country,
        postcode: s.customer.address.postcode,
        bankDetail: s.customer.bankDetail,
        accountHolderName: s.customer.bankDetail?.accountHolderName,
        accountNumber: s.customer.bankDetail?.accountNumber,
        sortCode: s.customer.bankDetail?.sortCode,
        businessPartnerId: s.customer.bankDetail?.businessPartnerId
    })); // consider more performant hook usage

    const onAmendPersonalDetailsButtonClick = () => {
        navigate(RoutePaths.AmendPersonalDetails);
    };

    const onAmendAddressButtonClick = () => {
        navigate(RoutePaths.AmendAddressDetails);
    };

    const onAmendBankDetailsButtonClick = () => {
        navigate(RoutePaths.AmendBankDetails, {
            state: { businessPartnerId: businessPartnerId }
        });
    };

    const onAmendEmailButtonClick = () => {
        navigate(RoutePaths.AmendEmailAddress);
    };

    const hasDirectDebitBankDetails = () => {
        return accountHolderName && accountNumber && sortCode;
    };

    return (
        <FormInput
            id='policyholderinfo'
            label={t('policy_holder_details_label')}
            helpText={t('help_text.policy_holder_info')}>
            <Selectable
                id={`policyholderdetails`}
                onClicked={() => onAmendPersonalDetailsButtonClick()}
                selected={false}
                hasSelectableButton={true}
                buttonText={t('amend')}
                style={{
                    border: '1px solid #ced4da',
                    marginTop: '10px'
                }}>
                <b>{t('title')}: </b>
                <span>{title}</span>
                <br />
                <b>{t('first_name')}: </b>
                <span>{firstName}</span>
                <br />
                <b>{t('middle_name')}: </b>
                <span>{middleName}</span>
                <br />
                <b>{t('last_name')}: </b>
                <span>{lastName}</span>
            </Selectable>
            <Selectable
                id={`policyholderaddressdetails`}
                onClicked={() => onAmendAddressButtonClick()}
                selected={false}
                hasSelectableButton={true}
                buttonText={t('amend')}
                style={{
                    border: '1px solid #ced4da',
                    marginTop: '10px'
                }}>
                <b>{t('address')}</b>
                <br />
                <b>{t('line_1_label')}</b>
                <span>{addressLine1}</span>
                <br />
                <b>{t('line_2_label')}</b>
                <span>{addressLine2}</span>
                <br />
                <b>{t('towncity_label')}: </b>
                <span>{townOrCity}</span>
                <br />
                <b>{t('county_label')}: </b>
                <span>{region}</span>
                <br />
                <b>{t('postcode')}: </b>
                <span>{postcode}</span>
                <br />
                <b>{t('country')}: </b>
                <span>{country}</span>
            </Selectable>
            <Selectable
                id={`policyholderemail`}
                onClicked={() => onAmendEmailButtonClick()}
                selected={false}
                hasSelectableButton={true}
                buttonText={t('amend')}
                style={{
                    border: '1px solid #ced4da',
                    marginTop: '10px'
                }}>
                <div className='div-text-align'>
                    <b>{t('email_address_label')}: </b>
                    <span>{email}</span>
                </div>
            </Selectable>
            {showBankDetails &&
                !!hasDirectDebitBankDetails() &&
                features.BankAccount === FeatureState.On && (
                    <Selectable
                        id={`policyholderbankdetails`}
                        onClicked={() => onAmendBankDetailsButtonClick()}
                        selected={false}
                        hasSelectableButton={true}
                        buttonText={t('amend')}
                        style={{
                            border: '1px solid #ced4da',
                            marginTop: '10px'
                        }}>
                        <BankDetailsInfo
                            title={t('direct_debit_bank_details')}
                            accountName={accountHolderName}
                            accountNumber={accountNumber}
                            sortCode={sortCode}
                            minimized
                        />
                    </Selectable>
                )}
        </FormInput>
    );
};

export default PolicyHolderInfo;
